import * as THREE from 'three';
import { getTrailSprite } from '../../visual/textures';
import { createMatteMaterial, fbm3d } from '../../visual';

const sparkVertex = /* glsl */ `
  attribute float aAge;
  attribute float aSize;
  attribute float aLife;
  varying float vLife;

  void main() {
    if (aAge < 0.0 || aAge >= aLife) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vLife = 0.0;
      return;
    }
    vLife = 1.0 - aAge / aLife;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    float t = 1.0 - vLife;
    float grow = smoothstep(0.0, 0.15, t) * smoothstep(1.0, 0.35, t);
    gl_PointSize = aSize * (0.5 + 1.1 * grow) * (320.0 / max(-mv.z, 0.1));
  }
`;

const sparkFragment = /* glsl */ `
  uniform sampler2D uMap;
  uniform vec3 uColorHot;
  uniform vec3 uColorCool;
  varying float vLife;

  void main() {
    if (vLife <= 0.0) discard;
    vec4 tex = texture2D(uMap, gl_PointCoord);
    float alpha = tex.a * (0.55 + 0.45 * vLife);
    if (alpha < 0.01) discard;
    vec3 color = mix(uColorCool, uColorHot, pow(vLife, 0.6));
    gl_FragColor = vec4(color, alpha);
  }
`;

const dustVertex = /* glsl */ `
  attribute float aAge;
  attribute float aSize;
  attribute float aLife;
  varying float vLife;

  void main() {
    if (aAge < 0.0 || aAge >= aLife) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vLife = 0.0;
      return;
    }
    vLife = 1.0 - aAge / aLife;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    float t = 1.0 - vLife;
    float grow = smoothstep(0.0, 0.5, t);
    gl_PointSize = aSize * (0.4 + 1.2 * grow) * (320.0 / max(-mv.z, 0.1));
  }
`;

const dustFragment = /* glsl */ `
  uniform sampler2D uMap;
  uniform vec3 uColor;
  varying float vLife;

  void main() {
    if (vLife <= 0.0) discard;
    vec4 tex = texture2D(uMap, gl_PointCoord);
    float fadeIn = smoothstep(0.0, 0.2, 1.0 - vLife);
    float alpha = tex.a * vLife * fadeIn * 0.42;
    if (alpha < 0.005) discard;
    gl_FragColor = vec4(uColor, alpha);
  }
`;

interface Shockwave {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.MeshBasicMaterial;
  active: boolean;
  time: number;
  duration: number;
  peakScale: number;
  initialOpacity: number;
}

interface Debris {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.MeshStandardMaterial;
  readonly velocity: THREE.Vector3;
  readonly angularAxis: THREE.Vector3;
  angularSpeed: number;
  baseScale: number;
  age: number;
  maxAge: number;
  active: boolean;
  baseShape: THREE.BufferGeometry | null;
}

export interface ShipExplosionPalette {
  readonly hull: number;
  readonly primary: number;
  readonly primaryDark: number;
  readonly accent: number;
  readonly trim: number;
  readonly glow: number;
}

const DEBRIS_SHAPE_COUNT = 6;
const DEBRIS_POOL_SIZE = 64;
const SHIP_DEBRIS_POOL_SIZE = 48;

export class Explosions {
  readonly id = 'explosions';
  readonly object3D: THREE.Group;

  private readonly sparks: THREE.Points;
  private readonly dust: THREE.Points;
  private readonly maxParticles: number;
  private readonly maxDust: number;
  private readonly sparkBuffers: ParticleBuffers;
  private readonly dustBuffers: ParticleBuffers;

  private readonly shockwaves: Shockwave[] = [];
  private readonly debrisPool: Debris[] = [];
  private readonly debrisShapes: THREE.BufferGeometry[] = [];
  private readonly shipDebrisPool: Debris[] = [];
  private readonly shipDebrisShapes: THREE.BufferGeometry[] = [];

  constructor(
    parent: THREE.Object3D,
    maxParticles = 520,
    maxShockwaves = 10,
    maxDust = 180
  ) {
    this.object3D = new THREE.Group();
    this.object3D.name = 'explosions';
    parent.add(this.object3D);

    this.maxParticles = maxParticles;
    this.maxDust = maxDust;

    this.sparkBuffers = createParticleBuffers(maxParticles);
    this.sparks = new THREE.Points(
      this.sparkBuffers.geometry,
      new THREE.ShaderMaterial({
        uniforms: {
          uMap: { value: getTrailSprite() },
          uColorHot: { value: new THREE.Color(0xfff2c0) },
          uColorCool: { value: new THREE.Color(0xff8a3a) }
        },
        vertexShader: sparkVertex,
        fragmentShader: sparkFragment,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      })
    );
    this.sparks.frustumCulled = false;
    this.object3D.add(this.sparks);

    this.dustBuffers = createParticleBuffers(maxDust);
    this.dust = new THREE.Points(
      this.dustBuffers.geometry,
      new THREE.ShaderMaterial({
        uniforms: {
          uMap: { value: getTrailSprite() },
          uColor: { value: new THREE.Color(0x3a2c26) }
        },
        vertexShader: dustVertex,
        fragmentShader: dustFragment,
        transparent: true,
        depthWrite: false,
        blending: THREE.NormalBlending
      })
    );
    this.dust.frustumCulled = false;
    this.object3D.add(this.dust);

    const ringGeo = new THREE.TorusGeometry(1, 0.06, 10, 48);
    ringGeo.rotateX(Math.PI / 2);
    for (let i = 0; i < maxShockwaves; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffd389,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        toneMapped: true
      });
      const mesh = new THREE.Mesh(ringGeo, mat);
      mesh.visible = false;
      this.object3D.add(mesh);
      this.shockwaves.push({
        mesh,
        material: mat,
        active: false,
        time: 0,
        duration: 1,
        peakScale: 1,
        initialOpacity: 0.9
      });
    }

    for (let i = 0; i < DEBRIS_SHAPE_COUNT; i++) {
      this.debrisShapes.push(buildDebrisShape());
    }
    for (let i = 0; i < DEBRIS_POOL_SIZE; i++) {
      const shape = this.debrisShapes[i % this.debrisShapes.length]!;
      const material = createMatteMaterial({
        color: 0x8684a6,
        roughness: 0.96,
        flatShading: true
      });
      const mesh = new THREE.Mesh(shape, material);
      mesh.visible = false;
      this.object3D.add(mesh);
      this.debrisPool.push({
        mesh,
        material,
        velocity: new THREE.Vector3(),
        angularAxis: new THREE.Vector3(0, 1, 0),
        angularSpeed: 0,
        baseScale: 1,
        age: 0,
        maxAge: 1,
        active: false,
        baseShape: null
      });
    }

    this.shipDebrisShapes.push(
      buildWingWedge(),
      buildEngineSegment(),
      buildNoseShard(),
      buildHullPanel(),
      buildBeam(),
      buildRingSliver()
    );
    for (let i = 0; i < SHIP_DEBRIS_POOL_SIZE; i++) {
      const material = createMatteMaterial({
        color: 0xcccccc,
        roughness: 0.55,
        flatShading: true
      });
      const mesh = new THREE.Mesh(this.shipDebrisShapes[0]!, material);
      mesh.visible = false;
      this.object3D.add(mesh);
      this.shipDebrisPool.push({
        mesh,
        material,
        velocity: new THREE.Vector3(),
        angularAxis: new THREE.Vector3(0, 1, 0),
        angularSpeed: 0,
        baseScale: 1,
        age: 0,
        maxAge: 1,
        active: false,
        baseShape: null
      });
    }
  }

  spawnDestruction(
    worldPos: THREE.Vector3,
    size: number,
    tint = 0x8684a6
  ): void {
    const radius = Math.max(size, 1);
    const count = Math.min(
      this.maxParticles,
      44 + Math.floor(size * 7)
    );
    this.emitBurst(this.sparkBuffers, worldPos, count, {
      speedMin: radius * 1.1,
      speedMax: radius * 3.6,
      lifeMin: 0.55,
      lifeMax: 1.1,
      sizeMin: 1.2,
      sizeMax: 2.8,
      posJitter: radius * 0.3
    });

    this.emitBurst(this.dustBuffers, worldPos, Math.min(this.maxDust, 26 + Math.floor(size * 3)), {
      speedMin: radius * 0.35,
      speedMax: radius * 1.2,
      lifeMin: 1.1,
      lifeMax: 1.9,
      sizeMin: 2.8,
      sizeMax: 5.4,
      posJitter: radius * 0.4
    });

    this.spawnShockwave(worldPos, radius * 2.0, 0.42, 0xffffff, 0.95);
    this.spawnShockwave(worldPos, radius * 4.4, 0.85, 0xffd389, 0.85);
    this.spawnShockwave(worldPos, radius * 6.8, 1.25, 0xb07c46, 0.38);

    this.spawnDebris(worldPos, size, tint);
  }

  spawnShipDestruction(
    worldPos: THREE.Vector3,
    size: number,
    palette: ShipExplosionPalette
  ): void {
    const radius = Math.max(size, 1.4);

    const sparkCount = Math.min(
      this.maxParticles,
      58 + Math.floor(size * 7)
    );
    this.emitBurst(this.sparkBuffers, worldPos, sparkCount, {
      speedMin: radius * 1.6,
      speedMax: radius * 4.4,
      lifeMin: 0.5,
      lifeMax: 1.2,
      sizeMin: 1.3,
      sizeMax: 3.1,
      posJitter: radius * 0.35
    });

    this.emitBurst(
      this.dustBuffers,
      worldPos,
      Math.min(this.maxDust, 20 + Math.floor(size * 2.2)),
      {
        speedMin: radius * 0.4,
        speedMax: radius * 1.1,
        lifeMin: 0.9,
        lifeMax: 1.7,
        sizeMin: 2.4,
        sizeMax: 4.8,
        posJitter: radius * 0.45
      }
    );

    this.spawnShockwave(worldPos, radius * 2.4, 0.38, 0xffffff, 0.95);
    this.spawnShockwave(worldPos, radius * 4.8, 0.8, palette.glow, 0.75);
    this.spawnShockwave(worldPos, radius * 6.4, 1.15, palette.primary, 0.45);

    this.spawnShipDebris(worldPos, size, palette);
  }

  spawnHit(worldPos: THREE.Vector3): void {
    this.emitBurst(this.sparkBuffers, worldPos, 8, {
      speedMin: 0.8,
      speedMax: 3.5,
      lifeMin: 0.25,
      lifeMax: 0.45,
      sizeMin: 0.45,
      sizeMax: 0.95,
      posJitter: 0.1
    });
  }

  update(dt: number): void {
    updateParticles(this.sparkBuffers, dt, 0.72);
    updateParticles(this.dustBuffers, dt, 0.55);
    this.updateShockwaves(dt);
    this.updateDebrisPool(this.debrisPool, dt);
    this.updateDebrisPool(this.shipDebrisPool, dt);
  }

  dispose(): void {
    this.sparks.geometry.dispose();
    (this.sparks.material as THREE.Material).dispose();
    this.dust.geometry.dispose();
    (this.dust.material as THREE.Material).dispose();
    for (const s of this.shockwaves) {
      s.mesh.geometry.dispose();
      s.material.dispose();
    }
    for (const shape of this.debrisShapes) shape.dispose();
    for (const d of this.debrisPool) d.material.dispose();
    for (const shape of this.shipDebrisShapes) shape.dispose();
    for (const d of this.shipDebrisPool) d.material.dispose();
  }

  private emitBurst(
    buffers: ParticleBuffers,
    worldPos: THREE.Vector3,
    count: number,
    opts: {
      speedMin: number;
      speedMax: number;
      lifeMin: number;
      lifeMax: number;
      sizeMin: number;
      sizeMax: number;
      posJitter: number;
    }
  ): void {
    for (let k = 0; k < count; k++) {
      const idx = buffers.cursor;
      const i3 = idx * 3;

      const phi = Math.random() * Math.PI * 2;
      const theta = Math.acos(2 * Math.random() - 1);
      const sinTheta = Math.sin(theta);
      const dx = sinTheta * Math.cos(phi);
      const dy = Math.cos(theta);
      const dz = sinTheta * Math.sin(phi);
      const speed =
        opts.speedMin + Math.random() * (opts.speedMax - opts.speedMin);

      buffers.positions[i3] =
        worldPos.x + (Math.random() - 0.5) * opts.posJitter;
      buffers.positions[i3 + 1] =
        worldPos.y + (Math.random() - 0.5) * opts.posJitter;
      buffers.positions[i3 + 2] =
        worldPos.z + (Math.random() - 0.5) * opts.posJitter;

      buffers.velocities[i3] = dx * speed;
      buffers.velocities[i3 + 1] = dy * speed;
      buffers.velocities[i3 + 2] = dz * speed;

      buffers.ages[idx] = 0;
      buffers.lives[idx] =
        opts.lifeMin + Math.random() * (opts.lifeMax - opts.lifeMin);
      buffers.sizes[idx] =
        opts.sizeMin + Math.random() * (opts.sizeMax - opts.sizeMin);

      buffers.cursor = (buffers.cursor + 1) % buffers.max;
    }
    buffers.positionAttr.needsUpdate = true;
    buffers.ageAttr.needsUpdate = true;
    buffers.sizeAttr.needsUpdate = true;
    buffers.lifeAttr.needsUpdate = true;
  }

  private spawnShockwave(
    worldPos: THREE.Vector3,
    peakScale: number,
    duration: number,
    color: number,
    initialOpacity = 0.9
  ): void {
    const free = this.shockwaves.find((s) => !s.active);
    if (!free) return;
    free.active = true;
    free.time = 0;
    free.duration = duration;
    free.peakScale = peakScale;
    free.initialOpacity = initialOpacity;
    free.mesh.position.copy(worldPos);
    free.mesh.scale.setScalar(0.05);
    free.mesh.rotation.set(
      (Math.random() - 0.5) * 0.6,
      Math.random() * Math.PI * 2,
      (Math.random() - 0.5) * 0.6
    );
    free.mesh.visible = true;
    free.material.color.setHex(color);
    free.material.opacity = initialOpacity;
  }

  private updateShockwaves(dt: number): void {
    for (const s of this.shockwaves) {
      if (!s.active) continue;
      s.time += dt;
      const t = s.time / s.duration;
      if (t >= 1) {
        s.active = false;
        s.mesh.visible = false;
        s.material.opacity = 0;
        continue;
      }
      const eased = 1 - Math.pow(1 - t, 2);
      const scale = 0.05 + eased * s.peakScale;
      s.mesh.scale.setScalar(scale);
      s.material.opacity = s.initialOpacity * Math.pow(1 - t, 1.4);
    }
  }

  private spawnDebris(
    worldPos: THREE.Vector3,
    size: number,
    tint: number
  ): void {
    const count = Math.min(
      this.debrisPool.length,
      7 + Math.floor(size * 2.2)
    );
    let spawned = 0;
    const color = new THREE.Color(tint);
    const shadow = color.clone().multiplyScalar(0.55);

    for (const chunk of this.debrisPool) {
      if (spawned >= count) break;
      if (chunk.active) continue;

      const shape =
        this.debrisShapes[Math.floor(Math.random() * this.debrisShapes.length)] ??
        this.debrisShapes[0]!;
      chunk.mesh.geometry = shape;
      chunk.material.color.copy(color).lerp(shadow, Math.random() * 0.55);
      chunk.material.emissive.setHex(0x000000);
      chunk.material.emissiveIntensity = 0;
      chunk.material.needsUpdate = true;

      const chunkSize = size * (0.09 + Math.random() * 0.22);
      chunk.baseScale = chunkSize;
      chunk.mesh.scale.setScalar(chunkSize);
      chunk.mesh.position.copy(worldPos).add(
        new THREE.Vector3(
          (Math.random() - 0.5) * size * 0.35,
          (Math.random() - 0.5) * size * 0.35,
          (Math.random() - 0.5) * size * 0.35
        )
      );
      chunk.mesh.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      chunk.mesh.visible = true;

      const dir = randomUnit();
      const speed = size * (1.0 + Math.random() * 2.4);
      chunk.velocity.copy(dir).multiplyScalar(speed);

      chunk.angularAxis.copy(randomUnit());
      chunk.angularSpeed = 3 + Math.random() * 9;

      chunk.age = 0;
      chunk.maxAge = 1.3 + Math.random() * 0.9;
      chunk.active = true;
      spawned++;
    }
  }

  private spawnShipDebris(
    worldPos: THREE.Vector3,
    size: number,
    palette: ShipExplosionPalette
  ): void {
    const count = Math.min(
      this.shipDebrisPool.length,
      10 + Math.floor(size * 2.5)
    );
    const tintPool = [
      palette.hull,
      palette.primary,
      palette.primary,
      palette.primaryDark,
      palette.trim,
      palette.accent
    ];
    const colorTmp = new THREE.Color();
    const shadowTmp = new THREE.Color();

    let spawned = 0;
    for (const chunk of this.shipDebrisPool) {
      if (spawned >= count) break;
      if (chunk.active) continue;

      const shape =
        this.shipDebrisShapes[
          Math.floor(Math.random() * this.shipDebrisShapes.length)
        ] ?? this.shipDebrisShapes[0]!;
      chunk.mesh.geometry = shape;
      chunk.baseShape = shape;

      const tint = tintPool[Math.floor(Math.random() * tintPool.length)] ?? palette.hull;
      colorTmp.setHex(tint);
      shadowTmp.copy(colorTmp).multiplyScalar(0.55);
      chunk.material.color.copy(colorTmp).lerp(shadowTmp, Math.random() * 0.3);
      chunk.material.emissive.setHex(palette.glow);
      chunk.material.emissiveIntensity = 0.25 + Math.random() * 0.2;
      chunk.material.needsUpdate = true;

      const chunkSize = size * (0.14 + Math.random() * 0.28);
      chunk.baseScale = chunkSize;
      chunk.mesh.scale.setScalar(chunkSize);
      chunk.mesh.position.copy(worldPos).add(
        new THREE.Vector3(
          (Math.random() - 0.5) * size * 0.35,
          (Math.random() - 0.5) * size * 0.35,
          (Math.random() - 0.5) * size * 0.35
        )
      );
      chunk.mesh.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      chunk.mesh.visible = true;

      const dir = randomUnit();
      const speed = size * (1.6 + Math.random() * 2.8);
      chunk.velocity.copy(dir).multiplyScalar(speed);

      chunk.angularAxis.copy(randomUnit());
      chunk.angularSpeed = 4 + Math.random() * 10;

      chunk.age = 0;
      chunk.maxAge = 1.5 + Math.random() * 1.1;
      chunk.active = true;
      spawned++;
    }
  }

  private updateDebrisPool(pool: Debris[], dt: number): void {
    const damping = Math.pow(0.72, dt);
    for (const chunk of pool) {
      if (!chunk.active) continue;
      chunk.age += dt;
      if (chunk.age >= chunk.maxAge) {
        chunk.active = false;
        chunk.mesh.visible = false;
        continue;
      }

      chunk.velocity.multiplyScalar(damping);
      chunk.mesh.position.addScaledVector(chunk.velocity, dt);
      chunk.mesh.rotateOnAxis(chunk.angularAxis, chunk.angularSpeed * dt);

      const t = chunk.age / chunk.maxAge;
      if (t > 0.6) {
        const shrink = 1 - (t - 0.6) / 0.4;
        chunk.mesh.scale.setScalar(chunk.baseScale * Math.max(shrink, 0));
      }

      if (chunk.material.emissiveIntensity > 0) {
        chunk.material.emissiveIntensity = Math.max(
          0,
          chunk.material.emissiveIntensity - dt * 0.6
        );
      }
    }
  }
}

interface ParticleBuffers {
  readonly max: number;
  readonly geometry: THREE.BufferGeometry;
  readonly positions: Float32Array;
  readonly velocities: Float32Array;
  readonly ages: Float32Array;
  readonly sizes: Float32Array;
  readonly lives: Float32Array;
  readonly positionAttr: THREE.BufferAttribute;
  readonly ageAttr: THREE.BufferAttribute;
  readonly sizeAttr: THREE.BufferAttribute;
  readonly lifeAttr: THREE.BufferAttribute;
  cursor: number;
}

function createParticleBuffers(max: number): ParticleBuffers {
  const positions = new Float32Array(max * 3);
  const velocities = new Float32Array(max * 3);
  const ages = new Float32Array(max);
  const sizes = new Float32Array(max);
  const lives = new Float32Array(max);
  for (let i = 0; i < max; i++) {
    ages[i] = -1;
    lives[i] = 1;
  }

  const geometry = new THREE.BufferGeometry();
  const positionAttr = new THREE.BufferAttribute(positions, 3);
  const ageAttr = new THREE.BufferAttribute(ages, 1);
  const sizeAttr = new THREE.BufferAttribute(sizes, 1);
  const lifeAttr = new THREE.BufferAttribute(lives, 1);
  positionAttr.setUsage(THREE.DynamicDrawUsage);
  ageAttr.setUsage(THREE.DynamicDrawUsage);
  sizeAttr.setUsage(THREE.DynamicDrawUsage);
  lifeAttr.setUsage(THREE.DynamicDrawUsage);

  geometry.setAttribute('position', positionAttr);
  geometry.setAttribute('aAge', ageAttr);
  geometry.setAttribute('aSize', sizeAttr);
  geometry.setAttribute('aLife', lifeAttr);
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

  return {
    max,
    geometry,
    positions,
    velocities,
    ages,
    sizes,
    lives,
    positionAttr,
    ageAttr,
    sizeAttr,
    lifeAttr,
    cursor: 0
  };
}

function updateParticles(
  buffers: ParticleBuffers,
  dt: number,
  dampingPerSecond: number
): void {
  let changed = false;
  const damping = Math.pow(dampingPerSecond, dt);
  for (let i = 0; i < buffers.max; i++) {
    const age = buffers.ages[i];
    const life = buffers.lives[i] ?? 1;
    if (age === undefined || age < 0 || age >= life) continue;
    buffers.ages[i] = age + dt;
    const i3 = i * 3;
    const vx = (buffers.velocities[i3] ?? 0) * damping;
    const vy = (buffers.velocities[i3 + 1] ?? 0) * damping;
    const vz = (buffers.velocities[i3 + 2] ?? 0) * damping;
    buffers.velocities[i3] = vx;
    buffers.velocities[i3 + 1] = vy;
    buffers.velocities[i3 + 2] = vz;
    buffers.positions[i3] = (buffers.positions[i3] ?? 0) + vx * dt;
    buffers.positions[i3 + 1] = (buffers.positions[i3 + 1] ?? 0) + vy * dt;
    buffers.positions[i3 + 2] = (buffers.positions[i3 + 2] ?? 0) + vz * dt;
    changed = true;
  }
  if (changed) {
    buffers.positionAttr.needsUpdate = true;
    buffers.ageAttr.needsUpdate = true;
  }
}

function buildDebrisShape(): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(1, 1);
  const position = geometry.attributes['position'] as THREE.BufferAttribute;
  const seed = new THREE.Vector3(
    Math.random() * 30,
    Math.random() * 30,
    Math.random() * 30
  );
  const axis = new THREE.Vector3(
    0.7 + Math.random() * 0.6,
    0.5 + Math.random() * 0.7,
    0.7 + Math.random() * 0.6
  );

  const dir = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    dir.set(position.getX(i), position.getY(i), position.getZ(i)).normalize();
    const n = fbm3d(
      dir.x * 1.8 + seed.x,
      dir.y * 1.8 + seed.y,
      dir.z * 1.8 + seed.z,
      3
    );
    const r = 1 + n * 0.35;
    position.setXYZ(
      i,
      dir.x * axis.x * r,
      dir.y * axis.y * r,
      dir.z * axis.z * r
    );
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

function randomUnit(): THREE.Vector3 {
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  return new THREE.Vector3(
    Math.sin(phi) * Math.cos(theta),
    Math.sin(phi) * Math.sin(theta),
    Math.cos(phi)
  );
}

function buildWingWedge(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(1.4, 0.18);
  shape.lineTo(1.1, 0.9);
  shape.lineTo(0.2, 0.75);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: 0.14,
    bevelEnabled: true,
    bevelSize: 0.04,
    bevelThickness: 0.04,
    bevelSegments: 1,
    steps: 1
  });
  geo.translate(-0.7, -0.45, -0.07);
  geo.computeVertexNormals();
  return geo;
}

function buildEngineSegment(): THREE.BufferGeometry {
  const geo = new THREE.CylinderGeometry(0.42, 0.48, 1.0, 14, 1, true);
  geo.rotateX(Math.PI / 2);
  return geo;
}

function buildNoseShard(): THREE.BufferGeometry {
  const geo = new THREE.ConeGeometry(0.45, 1.3, 6);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

function buildHullPanel(): THREE.BufferGeometry {
  const geo = new THREE.BoxGeometry(1.2, 0.18, 0.9);
  const pos = geo.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    pos.setX(i, pos.getX(i) + (Math.random() - 0.5) * 0.05);
    pos.setZ(i, pos.getZ(i) + (Math.random() - 0.5) * 0.05);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

function buildBeam(): THREE.BufferGeometry {
  const geo = new THREE.BoxGeometry(0.22, 0.22, 1.4);
  return geo;
}

function buildRingSliver(): THREE.BufferGeometry {
  const geo = new THREE.TorusGeometry(0.55, 0.09, 6, 10, Math.PI * 0.9);
  geo.rotateX(Math.PI / 2);
  return geo;
}
